/**
 * 新导入文档打开加速 - 异步轮询分页请求
 * weboffice 场景接入
 */
import { SlideFile, IMutationData, MutationSource, MutationType } from '@tencent/slide-sdk-model';
import type { ICommandData } from '@tencent/slide-sdk-model';
import { SlideDataServices, ILogger } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { ChunkDataManager } from '../../../core/managers/chunk-data-manager';
import { DocCreateStatus, IChunksGenerator } from '../../../core/interfaces/chunks-generator.interface';
import { SlideInitialDataType } from '../../../core/interfaces/slide-initial-data-service.interface';
import type { ISlideInitialData } from '../../../core/interfaces/slide-initial-data-service.interface';
import type { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import type { OnlineLoadConvertHandlers } from '../core/interfaces/online-edit-data-fetcher.interface';
import { IOnlineApiService } from '../core/interfaces/online-api-service.interface';
import { handleDetailError } from '../core/error/convert-error-notice';

interface IPollingResponse {
    // 是否需要终止轮询
    shouldTerminate?: boolean;
    // 后端控制轮询间隔
    asyncPollInterval?: number;
    // 轮询接口返回结果
    result?: any;
}

const ASYNC_POLLING_CODE = 408502;

// 每次最多同时请求资源数
const MAX_RESOURCE_COUNT = 10;
// 默认轮询间隔
const DEFAULT_DATA_POLLING_TIME = 16;
const DEFAULT_POLLING_TIME = 200;
// 总请求超时时间和后端保持一致默认5分钟
const DEFAULT_POLLING_TIMEOUT = 5 * 60 * 1000;
const LogTag = '[DocxOnlineAsyncDataFetcher]';

export class DocxOnlineAsyncDataFetcher {
    private chunkIndex = 0;
    private startIndex = 0;
    // 默认5页，根据首片后端返回页数调整一次
    private fetchMaxSlideCount = 5;
    private fetchDataFinished = false;
    private totalPageCount: number;
    private pendingResourceIds: string[];

    public constructor(
        private clientVarsService: IClientVarsService,
        private onlineApiService: IOnlineApiService,
        protected logger: ILogger,
    ) {
        this.pendingResourceIds = [];
    }

    public async loadPresentation(handlers: OnlineLoadConvertHandlers = {}): Promise<ISlideInitialData> {
        const chunkDataManager = new ChunkDataManager<{ commands: ICommandData[] }>();
        const chunksGenerator = chunkDataManager.toChunksGenerator().advance();
        chunksGenerator.updateDocCreateStatus(DocCreateStatus.NotCreate);

        return new Promise((resolve, reject) => {
            const dispose = (err?: Error) => {
                chunkDataManager.dispose(err);
                if (err) {
                    handlers.onFatalError?.(err);
                    reject(err);
                }
            };

            const handleHeadData = (headData: string[], totalPageCount: number, count: number) => {
                this.totalPageCount = totalPageCount;
                this.fetchMaxSlideCount = count;
                const totalChunkCount = Math.ceil(this.totalPageCount / this.fetchMaxSlideCount);
                handlers.onHead?.(totalChunkCount);
                resolve({
                    type: SlideInitialDataType.CommandChunkData,
                    data: {
                        head: this.getSlideFileJSON(headData, true),
                        chunks: chunksGenerator.init(totalChunkCount, this.totalPageCount),
                    },
                });
            };

            Promise.all([
                this.pollingTimeout(() => this.fetchChunkData(
                    chunkDataManager,
                    chunksGenerator,
                    handlers,
                    handleHeadData,
                ), 'fetchChunkData').finally(() => this.fetchDataFinished = true),
                this.pollingTimeout(() => this.fetchResourceData(), 'fetchResourceData').catch((error) => {
                    this.logger.error(`${LogTag} fetchResourceData catch error: `, error);
                }),
            ]).then(() => {
                // 分页 数据&资源 加载成功后，查询文档落盘状态
                handlers.onEnd?.(chunksGenerator.total);
                dispose();
                this.pollingTimeout(() => this.checkDocStatus(chunksGenerator), 'checkDocStatus').catch((error) => {
                    this.logger.error(`${LogTag} checkDocStatus catch error: `, error);
                });
            })
                .catch((error) => {
                    // 只有分片数据加载异常才 dispose error
                    dispose(error);
                });
        });
    }

    /**
     * 轮询请求文档数据
     * @param chunkDataManager
     * @param chunksGenerator
     * @param handlers
     * @param handleHeadData
     */
    private async fetchChunkData(
        chunkDataManager: ChunkDataManager<{ commands: ICommandData[]; }>,
        chunksGenerator: IChunksGenerator<{ commands: ICommandData[]; }>,
        handlers: OnlineLoadConvertHandlers,
        handleHeadData: (headData: string[], totalPagaCount: number, count: number) => void,
    ): Promise<IPollingResponse | void> {
        const { data: responseData } = await this.onlineApiService.getChunkData({
            padId: this.globalPadId,
            startIndex: this.startIndex,
            count: this.fetchMaxSlideCount,
            revisionVersion: this.requestRevision,
            notAlreadyCreate: 1,
        });
        const { retcode, data } = responseData;
        // 接口异常处理
        if (retcode && retcode !== ASYNC_POLLING_CODE) {
            throw handleDetailError(`Unexpected fetchChunkData response code: ${retcode}`, retcode);
        }
        if (data) {
            const {
                chunk_datas: chunkDatas,
                count,
                total: totalPageCount,
                resource_ids: resourceIds,
            } = data;
            // 处理数据
            if (chunkDatas && chunkDatas?.length > 0) {
                const newStartIndex = this.startIndex + count;
                if (this.chunkIndex === 0) {
                    handleHeadData(chunkDatas, totalPageCount, count);
                } else {
                    const totalChunkCount = this.calcTotalChunkCount(newStartIndex);
                    chunksGenerator.updateTotal(totalChunkCount);
                    chunkDataManager.add(this.chunkIndex, { commands: [this.getSlideFileJSON(chunkDatas, true)] });
                    handlers.onChunk?.(this.chunkIndex, totalChunkCount);
                }
                this.pendingResourceIds.push(...(resourceIds || []));
                this.startIndex = newStartIndex;
                this.chunkIndex += 1;
            }
            // 轮询结束
            if (this.startIndex >= this.totalPageCount) {
                return { shouldTerminate: true };
            }
        }
        return { asyncPollInterval: DEFAULT_DATA_POLLING_TIME };
    }

    /**
     * 轮询请求文档资源
     */
    private async fetchResourceData() {
        if (this.fetchDataFinished && this.pendingResourceIds.length === 0) {
            return { shouldTerminate: true };
        }
        if (this.pendingResourceIds.length === 0) {
            return;
        }
        const { data: responseData } = await this.onlineApiService.pollingResourceData(
            this.globalPadId,
            {
                resource_ids: JSON.stringify(this.pendingResourceIds.splice(0, MAX_RESOURCE_COUNT)),
            },
        );
        const { code, data } = responseData;
        if (code) {
            throw handleDetailError(`Unexpected fetchResourceData response code: ${code}`, code);
        }
        if (!data) {
            return;
        }
        const { resourceData, missingResources } = data;
        if (resourceData?.length && resourceData?.length > 0) {
            // update mutation
            const commandData = this.getSlideFileJSON(resourceData);
            const presentationStore = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
            presentationStore.applyCommandData(commandData, MutationSource.Keyframe);
        }
        this.pendingResourceIds.unshift(...(missingResources || []));
    }

    /**
     * 轮询请求查询文档是否落盘
     * 落盘成功后再创建 socket 连接
     */
    private async checkDocStatus(chunksGenerator: IChunksGenerator<{ commands: ICommandData[]; }>) {
        const { data: responseData } = await this.onlineApiService.pollingDocStatus(this.globalPadId);
        const { code, data } = responseData;

        if (code) {
            throw handleDetailError(`Unexpected checkDocStatus response code: ${code}`, code);
        }
        if (data?.importStatus) {
            chunksGenerator.updateDocCreateStatus(DocCreateStatus.AlreadyCreate);
            eventBus.emit(EventKeys.DOC_CREATED_SUCCESS);
            return {
                shouldTerminate: true,
            };
        }
    }

    private async pollingTimeout(
        pollingFn: () => Promise<IPollingResponse | void>,
        fnTag: string,
        timeout = DEFAULT_POLLING_TIMEOUT,
    ): Promise<any> {
        // 轮询结束后返回最终结果
        let finalResult;

        let shouldContinue = true;
        const startTime = Date.now();
        while (shouldContinue) {
            try {
                const { shouldTerminate, asyncPollInterval = DEFAULT_POLLING_TIME, result } = await pollingFn() || {};
                if (shouldTerminate) {
                    // 正常终止
                    finalResult = result;
                    shouldContinue = false;
                } else if (Date.now() - startTime > timeout) {
                    // 超时终止
                    throw new Error(`Total polling time exceeded: ${timeout}`);
                } else {
                    // 继续轮询
                    await new Promise(resolve => setTimeout(resolve, asyncPollInterval));
                }
            } catch (error) {
                this.logger.error(`${LogTag} ${fnTag} error message: `, error.message);
                shouldContinue = false;
                return Promise.reject(error);
            }
        }
        return Promise.resolve(finalResult);
    }

    private getSlideFileJSON(data: string[], isChunkData?: boolean): ICommandData {
        let finalMutations = [] as IMutationData[];
        data.forEach((currentData) => {
            const { mutations } = new SlideFile({ presentation: currentData }).getCommandData();
            if (isChunkData) {
                this.clearPreResourceMap(mutations);
            }
            finalMutations = finalMutations.concat(mutations);
        });
        return { mutations: finalMutations };
    }

    /**
     * 清除chunkData中的媒体资源预占位
     * TODO: 转换工具直接不返回
     * @param mutations
     */
    private clearPreResourceMap(mutations: IMutationData[]) {
        mutations.forEach((mutation) => {
            if (mutation?.type !== MutationType.UpdateResourceMap) {
                return;
            }
            const regex = /^(http|blob|data)/;
            if (mutation.updateResourceMap?.imageDescriptor?.src
                && !regex.test(mutation.updateResourceMap.imageDescriptor.src)) {
                mutation.updateResourceMap.imageDescriptor.src = '';
            }
        });
    }

    /**
     * 因为每片数据中请求的页数可能部分返回，所以需要重新计算分片总数
     * 已请求片数 + 剩余页数分片数
     * @param newStartIndex
     * @returns
     */
    private calcTotalChunkCount(newStartIndex: number): number {
        return (this.chunkIndex + 1) + Math.ceil((this.totalPageCount - newStartIndex) / this.fetchMaxSlideCount);
    }

    private get globalPadId(): string {
        return this.clientVarsService.getClientVars().collab_client_vars?.globalPadId ?? '';
    }

    private get requestRevision(): number {
        return this.clientVarsService.getClientVars().collab_client_vars?.rev || 0;
    }
}

